import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Button,
  Text,
  Flex,
} from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { useSearchArticles } from '@texas/components/shared/search/useSearchArticles';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GroupedOption,
  SelectFilterOption,
  ToggleFilterOption,
  GetFilterGroupedOptions,
} from '../../filterGroupedOptions';
import {
  groupStyles,
  groupBadgeStyles,
  articlesWidgetSelectStyles,
  SearchDropdownIndicator,
} from '../../styles';
import Select from 'react-select';
import { ArticleSearchProps } from '@texas/components/searchArticles/types';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { TFunction } from 'i18next';
import { emptyPagedItems } from '@texas/utils/helpers/commonHelpers';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import {
  saveBlobAsFile,
  excelExportFileName,
  exportPrefixes,
} from '@texas/utils/helpers/filesHelper';
import { searchApi } from '@texas/api/endpoints/search/searchApi';
import {
  SearchVariantItem,
  VariantsSearchQuery,
  defaultEmptyFilters,
} from '@texas/api/endpoints/search/searchVariantsTypes';
import { HttpClientResponse } from '@texas/api/httpClient/types';
import { TagFilterProvider } from '@texas/components/shared/tags/TagFilterContext';

export type OverviewType = 'default' | 'slim';

interface ModalProps extends SharedDisclosureProps {
  filters: VariantsSearchQuery;
  articleSearchProps: ArticleSearchProps;
  columns: (
    t: TFunction,
    slim?: boolean,
  ) => ColumnDef<SearchVariantItem, any>[];
  type?: OverviewType;
}

const pageSize = 12;

export function ArticlesOverviewModal({
  filters,
  articleSearchProps,
  columns,
  isOpen,
  onClose,
  type,
}: ModalProps) {
  const { t } = useTranslation();
  return (
    <Modal size="6xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW="1400px">
        <ModalHeader>
          {t('widgets.widgetResultOverview')}
          <Text
            fontFamily="Instrument Sans"
            fontWeight="normal"
            fontSize="md"
            color="gray.100"
            _light={{ color: 'texas.bg.800' }}
          >
            {t('widgets.filterDisclaimer')}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <InnerComponent
            filters={filters}
            articleSearchProps={articleSearchProps}
            columns={columns}
            onClose={onClose}
            type={type}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

interface InnerProps {
  filters: VariantsSearchQuery;
  articleSearchProps: ArticleSearchProps;
  columns: (
    t: TFunction,
    slim?: boolean,
  ) => ColumnDef<SearchVariantItem, any>[];
  onClose: () => void;
  type?: OverviewType;
}

function InnerComponent({
  filters,
  articleSearchProps,
  columns: widgetColumns,
  type,
}: InnerProps) {
  const { t } = useTranslation();

  const {
    availableFilters,
    setActiveFiltersVariantResults,
    setActiveFilterTypes,
    setSearchPage,
    filterElements,
    searchPage,
    setSearchParamsWithRef,
    searchParamsRef,
    error,
    articles,
    loading,
  } = useSearchArticles({
    defaultFilters: filters,
    defaultArticleSearchProps: articleSearchProps,
    optOutDefaultOrder: true,
    defaultPage: 1,
    limit: pageSize,
    request:
      type === 'slim' ? searchApi.searchVariantsSlim : searchApi.searchVariants,
    defaultData: { pagedItems: emptyPagedItems },
    type: type,
  });

  const columns = useMemo(() => {
    return widgetColumns(t);
  }, [widgetColumns, t]);

  const formatGroupLabel = (data: GroupedOption) => (
    <div style={groupStyles}>
      <span>{data.label}</span>
      <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
  );
  return (
    <>
      <Flex flexDir="column">
        <Flex alignItems="center" gap={2}>
          <Box flexShrink={0}>
            <Select<
              SelectFilterOption | ToggleFilterOption,
              false,
              GroupedOption
            >
              inputId="clickableInput"
              placeholder=""
              styles={articlesWidgetSelectStyles}
              components={{ DropdownIndicator: SearchDropdownIndicator }}
              options={GetFilterGroupedOptions(availableFilters)}
              formatGroupLabel={formatGroupLabel}
              onChange={(value) => {
                if (!value) return;
                const type = value.value;
                setActiveFiltersVariantResults((s) => [
                  ...s,
                  {
                    optionType: type,
                    ...defaultEmptyFilters,
                    isPopulated: false,
                  },
                ]);
                setActiveFilterTypes((s) => [...s, [true, type]]);
                setSearchPage((s) => ({
                  ...s,
                  filters: [...s.filters, type],
                }));
              }}
              value={null}
            />
          </Box>
          <Button
            variant="texas-light"
            ml="auto"
            leftIcon={<Icons.FileExcelBox boxSize={defaultIconSize} />}
            onClick={async () => {
              let response: HttpClientResponse<any>;
              if (type === 'slim') {
                response = await variantsApi.downloadExcelSlim(
                  articles?.exportArticleItems.map((x) => x.variantId) ?? [],
                );
              } else {
                response = await variantsApi.downloadExcel(
                  articles?.exportArticleItems.flatMap((x) => {
                    return x.branches.flatMap((b) => ({
                      variantId: x.variantId,
                      branchId: b.id,
                    }));
                  }) ?? [],
                );
              }
              if (response.hasError) return;

              saveBlobAsFile(
                response.data,
                excelExportFileName(exportPrefixes.articles),
              );
            }}
            isDisabled={loading || articles?.pagedItems.totalItems == 0}
          >
            {t('general.exportExcel')}
          </Button>
        </Flex>

        <Box
          width="100%"
          display="grid"
          gridTemplateColumns="repeat(6, 1fr)"
          gridGap={1}
          rowGap={1}
        >
          <Box height="fit-content" mt="auto" gridColumn="span 2">
            <SearchFilterInput
              placeholder={t('searchArticles.itemcodeOrDescription')}
              debounceDelay={150}
              value={searchPage.searchParams.searchTerm}
              onChange={(searchTerm) =>
                setSearchParamsWithRef({
                  ...searchParamsRef.current,
                  searchTerm,
                  page: 1,
                })
              }
            />
          </Box>
          {filterElements}
        </Box>
      </Flex>
      <TagFilterProvider>
        <DataTableContainer
          p={4}
          w="100%"
          minH={0}
          display="flex"
          flexDir="column"
          error={error}
          css={{ ' th, td': { whiteSpace: 'normal' } }}
          tableId="articles-overview-modal"
          datatable={{
            data: articles?.pagedItems.items ?? [],
            columns: columns,
            isLoading: loading,
            rowProps: {
              getRowId: (originalRow, _) => {
                return originalRow.articleId.toString();
              },
            },
            sorted: {
              onSortedChange: ({ key, desc }) => {
                setSearchParamsWithRef({
                  ...searchParamsRef.current,
                  sortBy: key,
                  sortDesc: desc,
                  page: 1,
                });
              },
              key: searchPage.searchParams['sortBy'],
              desc: searchPage.searchParams['sortDesc'],
            },
          }}
          pagination={{
            totalItems: articles?.pagedItems.totalItems ?? 0,
            pageSize: pageSize,
            currentPage: searchPage.searchParams.page,
            onPageChange: (page) =>
              setSearchParamsWithRef({
                ...searchParamsRef.current,
                page,
              }),
          }}
        />
      </TagFilterProvider>
    </>
  );
}
